/**
 * 侧边栏菜单组件 - 使用 Ant Design Menu 和 Layout
 */

import React, { useEffect, useState } from 'react';
import { Menu } from 'antd';
import { useNavigate, useLocation } from 'react-router-dom';
import { useMenuDataSimple } from '../../../../hooks/useMenuDataSimple';
import { eventBus } from '../../../../utils/eventBus';
import './Sidebar.css';

interface SidebarProps {
  collapsed: boolean;
  selectedModule: string;
  onModuleChange: (module: string) => void;
}

const Sidebar: React.FC<SidebarProps> = ({ collapsed, selectedModule, onModuleChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { menuData, loading, buildMenuTree, convertToMenuItems, loadMenuData } = useMenuDataSimple();
  const [menuItems, setMenuItems] = useState<any[]>([]);
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);

  // 确保菜单数据已加载
  useEffect(() => {
    // 如果菜单数据为空且不在加载中，尝试从全局缓存读取或加载
    if (menuData.length === 0 && !loading) {
      // 先尝试从全局缓存读取
      let currentUserId: number | null = null;
      try {
        const userInfoStr = localStorage.getItem('user_info');
        if (userInfoStr) {
          const userInfo = JSON.parse(userInfoStr);
          currentUserId = userInfo?.id || null;
        }
      } catch (e) {
        // 忽略解析错误
      }
      
      const cacheKey: number | string | null = currentUserId;
      if (cacheKey) {
        const globalMenuDataMap = (window as any).__globalMenuDataMap__;
        const globalIsLoadedMap = (window as any).__globalIsLoadedMap__;
        
        if (globalMenuDataMap && globalIsLoadedMap) {
          const isLoaded = globalIsLoadedMap.get(cacheKey) || false;
          const cachedData = globalMenuDataMap.get(cacheKey);
          
          if (isLoaded && cachedData && cachedData.length > 0) {
            // 如果全局缓存有数据，但 menuData 为空，说明状态没有同步
            // 需要触发重新加载或手动更新（但这里不能直接设置，因为 menuData 是 hook 的返回值）
            // 所以调用 loadMenuData，它会检查缓存并使用缓存数据
            loadMenuData();
            return;
          }
        }
      }
      
      // 如果缓存中没有数据，尝试加载
      loadMenuData();
    }
  }, [menuData.length, loading, loadMenuData]);

  // 构建菜单项
  useEffect(() => {
    if (menuData.length > 0) {
      const treeData = buildMenuTree(menuData);
      const items = convertToMenuItems(treeData);

      // 处理图标渲染和菜单点击
      const processMenuItems = (items: any[]): any[] => {
        return items.map(item => {
          // 判断是否为分类（有子菜单）
          const hasChildren = item.children && Array.isArray(item.children) && item.children.length > 0;
          
          // 获取菜单名称（从 _menuName 或 label 获取）
          const menuName = item._menuName || item.label || '';
          
          // 递归处理子菜单（如果有）
          const processedChildren = hasChildren ? processMenuItems(item.children) : undefined;
          
          // 创建菜单项，只包含 Ant Design Menu 需要的属性
          const menuItem: any = {
            key: item.key,
            path: item.path, // 确保path信息被保留
            icon: item.icon ? (
              <i 
                className="iconfont" 
                dangerouslySetInnerHTML={{ __html: item.icon }}
                style={{ fontSize: 16, marginRight: 8 }}
              />
            ) : (
              <i className="iconfont" style={{ fontSize: 16, marginRight: 8 }}>
                {menuName === '用户管理' ? '&#xe7ae;' : 
                 menuName === '角色管理' ? '&#xe7b0;' :
                 menuName === '菜单管理' ? '&#xe7b1;' :
                 menuName === '字典管理' ? '&#xe7b2;' : '&#xe7b3;'}
              </i>
            ),
            label: item.label,
            onClick: () => {
              // 只有菜单（非分类）才处理点击事件
              if (!hasChildren && item.path) {
                
                // 立即设置选中状态
                setSelectedKeys([item.key]);
                
                // 直接使用数据库中的路径，无需硬编码映射
                if (item.path && item.path.startsWith('/')) {
                  navigate(item.path);
                }
              }
            },
            // 如果有子菜单，递归处理并设置children
            children: processedChildren,
          };
          
          return menuItem;
        });
      };
      
      const processedItems = processMenuItems(items);
      
      // 只在构建失败时输出警告
      if (processedItems.length === 0 && menuData.length > 0) {
        console.warn('[Sidebar] 菜单数据已加载，但构建后的菜单项为空', {
          menuDataLength: menuData.length,
          treeDataLength: treeData.length,
          itemsLength: items.length
        });
      }
      
      setMenuItems(processedItems);
      
      // 不要在这里设置默认的展开keys，让路由变化时再决定展开哪些菜单
      // 这样可以避免与路由匹配逻辑冲突
    } else {
      // 菜单数据为空时，清空菜单项
      // 不输出警告，因为可能是正常加载中状态
      setMenuItems([]);
    }
  }, [menuData, buildMenuTree, convertToMenuItems, loading]); // 依赖 menuData 和相关函数

  // 根据当前URL路径自动设置菜单选中状态和展开状态
  useEffect(() => {
    // 确保菜单数据已加载
    if (menuItems.length > 0 && !loading) {
      const currentPath = location.pathname;
      
      // 统一的路径匹配函数（与 BreadcrumbNav 保持一致）
      const findMenuByPath = (items: any[], targetPath: string, parentKeys: string[] = []): { item: any, parentKeys: string[] } | null => {
        for (const item of items) {
          const itemPath = item.path || '';
          const currentParentKeys = [...parentKeys];
          
          // 标准化路径进行比较（去掉尾部斜杠）
          const normalizePath = (p: string) => p.replace(/\/$/, '') || '/';
          const normalizedItemPath = normalizePath(itemPath);
          const normalizedTargetPath = normalizePath(targetPath);
          
          // 检查路径是否匹配（支持完全匹配）
          const isExactMatch = normalizedItemPath === normalizedTargetPath;
          
          // 如果当前项路径匹配（不需要检查菜单类型，只要路径匹配就选中）
          if (isExactMatch && itemPath) {
            return { item, parentKeys: currentParentKeys };
          }
          
          // 检查子菜单
          if (item.children && item.children.length > 0) {
            // 如果有子菜单，将当前项的key加入父级keys
            const newParentKeys = [...currentParentKeys, item.key];
            const childResult = findMenuByPath(item.children, targetPath, newParentKeys);
            if (childResult) {
              return childResult;
            }
          }
        }
        return null;
      };
      
      // 直接从菜单数据中查找匹配的菜单项（只使用路径匹配，不使用硬编码映射）
      const menuResult = findMenuByPath(menuItems, currentPath);
      
      if (menuResult) {
        // 设置选中的菜单项
        setSelectedKeys([menuResult.item.key]);
        
        // 构建展开的菜单keys（包含所有父级菜单）
        const allOpenKeys = [...menuResult.parentKeys];
        
        // 确保所有父级菜单都被展开
        menuResult.parentKeys.forEach(key => {
          if (key && !allOpenKeys.includes(key)) {
            allOpenKeys.push(key);
          }
        });
        
        const finalOpenKeys = Array.from(new Set(allOpenKeys)).filter(Boolean);
        if (finalOpenKeys.length > 0) {
          setOpenKeys(finalOpenKeys);
        }
      } else {
        // 如果没有找到匹配的菜单项，清空选中状态，但保持当前展开状态
        setSelectedKeys([]);
      }
    }
  }, [location.pathname, menuItems, loading]);

  // 根据选中的模块智能展开菜单（保留原有逻辑作为备用）
  useEffect(() => {
    if (menuItems.length > 0 && selectedModule && !location.pathname.includes(selectedModule)) {
      // 查找当前选中模块对应的菜单项
      const findMenuPath = (items: any[], targetModule: string): string[] => {
        for (const item of items) {
          // 检查当前项是否匹配
          const menuName = item._menuName || item.label || '';
          if (item.key === targetModule || menuName === targetModule) {
            return [item.key];
          }
          
          // 检查子菜单
          if (item.children && item.children.length > 0) {
            const childPath = findMenuPath(item.children, targetModule);
            if (childPath.length > 0) {
              return [item.key, ...childPath];
            }
          }
        }
        return [];
      };

      const menuPath = findMenuPath(menuItems, selectedModule);
      
      if (menuPath.length > 0) {
        // 只展开父级菜单，不展开子菜单
        const parentKeys = menuPath.slice(0, -1);
        
        // 保留菜单功能列表的展开状态
        const functionMenuKeys = menuItems
          .filter(item => {
            const menuName = item._menuName || item.label || '';
            const menuType = item._menuType || '';
            return menuName === '菜单功能' || menuType === 'function';
          })
          .map(item => item.key);
        
        // 合并父级菜单和菜单功能列表
        const allKeys = [...parentKeys, ...functionMenuKeys];
        const finalOpenKeys = Array.from(new Set(allKeys));
        
        setOpenKeys(finalOpenKeys);
        setSelectedKeys([menuPath[menuPath.length - 1]]);
      }
    }
  }, [selectedModule, menuItems, location.pathname]);

  // 清理菜单项中的私有属性，避免传递给 DOM
  const cleanMenuItemsForRender = (items: any[]): any[] => {
    return items.map(item => {
      const cleanItem: any = {
        key: item.key,
        icon: item.icon,
        label: item.label,
        onClick: item.onClick,
      };
      
      // 如果有子菜单，递归清理（确保空数组也被视为有效）
      if (item.children && Array.isArray(item.children) && item.children.length > 0) {
        const cleanedChildren = cleanMenuItemsForRender(item.children);
        if (cleanedChildren.length > 0) {
          cleanItem.children = cleanedChildren;
        }
      }
      
      return cleanItem;
    });
  };

  // 处理菜单展开/收缩
  const handleOpenChange = (keys: string[]) => {
    
    // 确保菜单功能列表始终保持展开
    const functionMenuKeys = menuItems
      .filter(item => {
        const menuName = item._menuName || item.label || '';
        const menuType = item._menuType || '';
        return menuName === '菜单功能' || menuType === 'function';
      })
      .map(item => item.key);
    
    // 合并用户操作和菜单功能列表
    const allKeys = [...keys, ...functionMenuKeys];
    const finalKeys = Array.from(new Set(allKeys));
    
    setOpenKeys(finalKeys);
  };

  // 菜单变更事件监听已由全局状态管理器处理，无需额外监听

  return (
    <div className={`sidebar ${collapsed ? 'collapsed' : ''}`}>
      <div className="sidebar-header">
        <div className="logo">
          {collapsed ? (
            <img src="/images/log.png" alt="Logo" className="logo-image-small" />
          ) : (
            <div className="logo-content">
              <img src="/images/log.png" alt="" className="logo-image" />
              <span className="logo-text">数据维护平台</span>
            </div>
          )}
        </div>
      </div>

      <div className="sidebar-menu">
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          items={cleanMenuItemsForRender(menuItems)}
          // 移除重复的onClick处理，避免与processMenuItems中的onClick冲突
          className="sidebar-menu-content"
          // 确保菜单支持多级展开
          multiple={false}
        />
      </div>
    </div>
  );
};

export default Sidebar;
